import heic2any from 'heic2any';
import { RcFile } from 'antd/es/upload';

export const convertHeicToJpeg = async (file: File): Promise<File | undefined> => {
    const buffer = await file.arrayBuffer();
    const blob = new Blob([buffer], { type: 'image/heic' });

    try {
        const jpgBlob = (await heic2any({ blob, toType: 'image/jpeg' })) as Blob; // Convert HEIC to JPEG
        return new File([jpgBlob], file.name.replace(/\.HEIC$/i, '.jpeg'), { type: 'image/jpeg' });
    } catch (error: any) {
        if (error.code === 1 && error.message.includes('image/jpeg')) {
            return new File([blob], file.name.replace(/\.HEIC$/i, '.jpeg'), { type: 'image/jpeg' });
        }
        return undefined;
    }
};

const getExtension = (fileName: string) => {
    const re = /(?:\.([^.]+))?$/;
    return re.exec(fileName)![1];
};

export const getFileExtension = (fileName: string) => {
    const ext = getExtension(fileName);
    return ext === undefined ? 'NA' : ext.toUpperCase().trim();
};

export const getFileName = (fileName: string) => {
    if (!fileName) return fileName;
    const idx = fileName.lastIndexOf('.');
    return idx >= 0 ? fileName.substr(0, fileName.lastIndexOf('.')) : fileName;
};

// suspicious if file has empty name or no extension
export const hasSuspiciousFileName = (fileName: string) => {
    const nameWithoutExtension = getFileName(fileName);
    return nameWithoutExtension === '' || !fileName.includes('.');
};

// note that some extensions do not have mime types like scn and tpl
export const FILE_MIME_TYPE: { [key: string]: string | string[] } = {
    // images
    JPG: 'image/jpeg',
    JPEG: 'image/jpeg',
    BMP: 'image/bmp',
    GIF: 'image/gif',
    PNG: 'image/png',
    HEIC: ['image/heic', ''], // windows mime type is empty for heic files

    // audio
    MP3: 'audio/mpeg',
    WAV: 'audio/wav',
    M4A: 'audio/m4a',
    AAC: 'audio/aac',

    // video
    WMV: 'video/x-ms-wmv',
    MOV: 'video/quicktime',
    AVI: 'video/x-msvideo',
    FLV: 'video/x-flv',
    MP4: 'video/mp4',

    // word
    DOC: 'application/msword',
    DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

    // excel
    XLS: 'application/vnd.ms-excel',
    XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

    // ppt
    PPT: 'application/vnd.ms-powerpoint',
    PPTX: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

    // model
    OBJ: 'model/obj',
    ZIP: ['application/zip', 'application/x-zip-compressed', 'application/octet-stream'],

    // some ifc sample files have empty file types
    // IFC: 'application/x-step',

    // scan
    GLB: ['model/gltf-binary', ''], // glb files exported from app has empty type

    // others
    CSV: 'text/csv',
    PDF: 'application/pdf',
    TXT: 'text/plain',
};

// not all files have mime types. if mime type is available, check that extension matches mime type
export const hasSuspiciousMimeType = (file: RcFile) => {
    const fileExt = getFileExtension(file.name);
    if (fileExt && FILE_MIME_TYPE[fileExt]) {
        if (Array.isArray(FILE_MIME_TYPE[fileExt])) {
            return !FILE_MIME_TYPE[fileExt].includes(file.type);
        }
        return file.type !== FILE_MIME_TYPE[fileExt];
    }
    return false;
};

export const isMaliciousFile = (file: RcFile) => hasSuspiciousFileName(file.name) || hasSuspiciousMimeType(file);
